import GuideLayout from "../components/GuideLayout";
import { useLang } from "../i18n/LanguageContext";
import { faqPageLd } from "../lib/schema";

const HERO =
  "https://static.prod-images.emergentagent.com/jobs/c98acbe0-6e23-474e-adcd-5ab166743bcb/images/c56e991f22c918113dadc50b66a24033f3fa7335b4f69674f1d85669bfd49e8c.jpeg";

const jsonLd = {
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: "Private yacht transfer (NCC)",
  name: "Viareggio & Carrara Yacht Transfers",
  provider: { "@id": "https://yourblacklimo.com/#business" },
  areaServed: ["Viareggio", "Carrara", "Marina di Carrara", "Pisa", "Florence", "Lucca", "Tuscany"],
  description:
    "Discreet private chauffeur transfers for yacht owners, guests and crew at the marinas of Viareggio and Carrara — to airports, cities and anywhere in Tuscany, in a Mercedes V-Class.",
  url: "https://yourblacklimo.com/viareggio-carrara-yacht-transfers",
};

const CONTENT = {
  en: {
    seoTitle: "Viareggio & Carrara Yacht Transfers | Private Chauffeur for Owners & Crew — Your Black Limo",
    seoDesc:
      "Discreet private chauffeur transfers at the yacht marinas of Viareggio and Carrara — for owners, guests and crew. Airport connections, provisioning runs and day trips across Tuscany in a Mercedes V-Class.",
    overline: "Yacht Transfers",
    h1: "Private Yacht Transfers — Viareggio & Carrara",
    intro:
      "Discreet, punctual transfers between your yacht and anywhere in Tuscany. For owners, guests and crew alike, we handle airport connections, guest day trips and quiet logistics with the confidentiality the marina world expects — in a spotless Mercedes V-Class.",
    heroAlt: "Yachts moored in an Italian marina harbour on the Tuscan coast",
    blocks: [
      {
        title: "Marina di Viareggio",
        body: "The historic heart of Italian yacht building and one of the Riviera's most elegant marinas. We connect the docks of Viareggio to Pisa and Florence airports, to Lucca and the Versilia coast, and to any address across Tuscany — quietly and on schedule.",
      },
      {
        title: "Marina di Carrara",
        body: "Home to superyacht shipyards beneath the marble mountains. From the Carrara docks we arrange smooth transfers to airports, to the Cinque Terre and along the coast, coordinating with captains and guest schedules down to the minute.",
      },
    ],
    cards: {
      overline: "For the marina",
      title: "How we serve yacht owners and crew",
      items: [
        { title: "Airport connections", body: "Seamless private transfers between the marina and the airports of Pisa, Florence, and beyond." },
        { title: "Guest transfers & day trips", body: "Bespoke days ashore for your guests — Florence, Lucca, Chianti wine country and the coast." },
        { title: "Crew & provisioning runs", body: "Reliable, low-profile support for crew logistics and shore errands, on your timetable." },
        { title: "Coast-to-city transfers", body: "Direct links to the cities, the Cinque Terre and onward destinations along the Italian coast." },
      ],
    },
    note: {
      title: "For owners, guests and crew",
      body:
        "We understand that marina schedules move and that discretion is everything. Your chauffeur is punctual, immaculately presented and completely confidential — comfortable coordinating with captains and stewardesses, and equally at ease welcoming owners and their guests. Fully licensed NCC, professional English-speaking drivers, and a Mercedes V-Class for up to seven passengers with luggage.",
    },
    cta: {
      title: "Arrange discreet transfers at Viareggio or Carrara",
      secondaryLabel: "See all services",
      secondaryHref: "/services",
      primaryLabel: "Request a quote",
      primaryHref: "/#contact",
    },
    faq: {
      overline: "Questions",
      title: "Frequently asked questions",
      items: [
        { q: "Do you coordinate with the captain and crew?", a: "Absolutely. We coordinate timings directly with captains and stewardesses and adapt instantly when marina schedules change." },
        { q: "Which marinas do you serve?", a: "Primarily Viareggio and Marina di Carrara, with connections to airports, cities, the Cinque Terre and anywhere along the Tuscan and Ligurian coast." },
        { q: "Can you handle provisioning and crew errands?", a: "Yes. We provide discreet, reliable support for crew logistics and shore errands, on your timetable." },
        { q: "How many passengers and how much luggage?", a: "Up to seven passengers with luggage in a Mercedes V-Class, ideal for owners, guests and crew alike." },
      ],
    },
  },
  it: {
    seoTitle: "Transfer Yacht Viareggio e Carrara | Autista Privato per Armatori ed Equipaggio — Your Black Limo",
    seoDesc:
      "Transfer privati e discreti con autista alle marine per yacht di Viareggio e Carrara — per armatori, ospiti ed equipaggio. Collegamenti aeroportuali, provviste e gite in Toscana in Mercedes Classe V.",
    overline: "Transfer Yacht",
    h1: "Transfer Privati per Yacht — Viareggio e Carrara",
    intro:
      "Transfer discreti e puntuali tra il tuo yacht e ogni angolo della Toscana. Per armatori, ospiti ed equipaggio, gestiamo collegamenti aeroportuali, gite per gli ospiti e la logistica silenziosa con la riservatezza che il mondo delle marine richiede — a bordo di un'impeccabile Mercedes Classe V.",
    heroAlt: "Yacht ormeggiati in un porto turistico italiano sulla costa toscana",
    blocks: [
      {
        title: "Marina di Viareggio",
        body: "Il cuore storico della nautica italiana e una delle marine più eleganti della Riviera. Colleghiamo le banchine di Viareggio agli aeroporti di Pisa e Firenze, a Lucca e alla Versilia, e a qualsiasi indirizzo in Toscana — con discrezione e puntualità.",
      },
      {
        title: "Marina di Carrara",
        body: "Sede di cantieri per superyacht ai piedi delle montagne di marmo. Dalle banchine di Carrara organizziamo transfer fluidi verso gli aeroporti, le Cinque Terre e lungo la costa, coordinandoci con comandanti e programmi degli ospiti al minuto.",
      },
    ],
    cards: {
      overline: "Per la marina",
      title: "Come serviamo armatori ed equipaggi",
      items: [
        { title: "Collegamenti aeroportuali", body: "Transfer privati fluidi tra la marina e gli aeroporti di Pisa, Firenze e oltre." },
        { title: "Ospiti e gite in giornata", body: "Giornate a terra su misura per i tuoi ospiti — Firenze, Lucca, il Chianti e la costa." },
        { title: "Equipaggio e provviste", body: "Supporto affidabile e riservato per la logistica dell'equipaggio e le commissioni, sui tuoi tempi." },
        { title: "Dalla costa alle città", body: "Collegamenti diretti alle città, alle Cinque Terre e alle destinazioni lungo la costa italiana." },
      ],
    },
    note: {
      title: "Per armatori, ospiti ed equipaggio",
      body:
        "Sappiamo che gli orari delle marine cambiano e che la discrezione è tutto. Il tuo autista è puntuale, impeccabile nella presenza e assolutamente riservato — a suo agio nel coordinarsi con comandanti e hostess di bordo, e altrettanto nell'accogliere armatori e ospiti. NCC regolarmente autorizzato, autisti professionisti che parlano inglese e una Mercedes Classe V fino a sette passeggeri con bagagli.",
    },
    cta: {
      title: "Organizza transfer discreti a Viareggio o Carrara",
      secondaryLabel: "Tutti i servizi",
      secondaryHref: "/services",
      primaryLabel: "Richiedi un preventivo",
      primaryHref: "/#contact",
    },
    faq: {
      overline: "Domande",
      title: "Domande frequenti",
      items: [
        { q: "Vi coordinate con il comandante e l'equipaggio?", a: "Assolutamente. Ci coordiniamo direttamente con comandanti e hostess di bordo sugli orari e ci adattiamo all'istante quando i programmi della marina cambiano." },
        { q: "Quali marine servite?", a: "Principalmente Viareggio e Marina di Carrara, con collegamenti ad aeroporti, città, Cinque Terre e ovunque lungo la costa toscana e ligure." },
        { q: "Potete gestire provviste e commissioni per l'equipaggio?", a: "Sì. Offriamo un supporto discreto e affidabile per la logistica dell'equipaggio e le commissioni a terra, sui vostri tempi." },
        { q: "Quanti passeggeri e quanto bagaglio?", a: "Fino a sette passeggeri con bagagli in una Mercedes Classe V, ideale per armatori, ospiti ed equipaggio." },
      ],
    },
  },
};

export default function YachtTransferGuide() {
  const { lang } = useLang();
  const c = CONTENT[lang === "it" ? "it" : "en"];
  const { ["@context"]: _ctx, ...serviceNode } = jsonLd;
  const graphLd = { "@context": "https://schema.org", "@graph": [serviceNode, faqPageLd(c.faq.items)] };
  return (
    <GuideLayout
      testid="yacht-transfer-guide"
      faq={c.faq}
      seo={{ title: c.seoTitle, description: c.seoDesc, path: "/viareggio-carrara-yacht-transfers", locale: lang === "it" ? "it_IT" : "en_US", jsonLd: graphLd }}
      overline={c.overline}
      h1={c.h1}
      intro={c.intro}
      heroImage={HERO}
      heroAlt={c.heroAlt}
      blocks={c.blocks}
      cards={c.cards}
      note={c.note}
      cta={c.cta}
    />
  );
}
